import React, { useState } from 'react';
import { X, Calendar, Clock, Building2, User, Mail, Phone, MessageSquare, CheckCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import js from '@eslint/js';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  'Clinical Trial Management',
  'Biostatistics & Data Management',
  'Regulatory Affairs',
  'Medical Writing',
  'Pharmacovigilance',
  'Drug Recall Management',
  'Sclin EDC Platform',
  'DocuDefender eTMF',
  'General Inquiry'
];

const timeSlots = [
  '9:00 AM EST',
  '10:00 AM EST',
  '11:00 AM EST',
  '1:00 PM EST',
  '2:00 PM EST',
  '3:00 PM EST',
  '4:00 PM EST'
];

export const DemoModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    service_interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    // console.log('Submitting demo request with data:', JSON.stringify(formData, null, 2));
    try {
      await axios.post(`https://vetforcesolutions.com/backend/api/hiring_requests/demo_request_submit`, formData);
      setIsSuccess(true);
    } catch (err) {
      setError('Failed to submit request. Please try again or call 407-777-2216.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsSuccess(false);
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      preferred_date: '',
      preferred_time: '',
      service_interest: '',
      message: ''
    });
    onClose();
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
          onClick={handleClose}
          data-testid="demo-modal-overlay"
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg"
            style={{
              background: 'rgba(13, 17, 23, 0.98)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
            data-testid="demo-modal"
          >
            {/* Header */}
            <div className="sticky top-0 px-5 py-4 flex items-center justify-between z-10 border-b border-white/5"
              style={{ background: 'rgba(13, 17, 23, 0.98)' }}>
              <div>
                <h2 className="text-lg font-outfit font-bold text-silver-shine">Request a Demo</h2>
                <p className="text-xs text-slate-500">Schedule a personalized consultation</p>
              </div>
              <button 
                onClick={handleClose}
                className="text-slate-500 hover:text-silver-shine transition-colors p-1"
                data-testid="close-demo-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5">
              {isSuccess ? (
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-8"
                >
                  <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-silver-shine mb-2">Demo Requested!</h3>
                  <p className="text-slate-500 text-sm mb-6">
                    Our team will contact you within 24 hours.
                  </p>
                  <button onClick={handleClose} className="btn-primary px-6 py-2 text-sm" data-testid="demo-success-close">
                    Close
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="px-3 py-2 rounded-lg text-xs text-red-300 bg-red-500/10 border border-red-500/15">
                      {error}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    {/* Name */}
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        <User className="w-3 h-3 text-blue-400" />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="demo-name-input"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        <Mail className="w-3 h-3 text-blue-400" />
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="demo-email-input"
                      />
                    </div>

                    {/* Company */}
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 text-[#8F262B]" />
                        Company *
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="demo-company-input"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        <Phone className="w-3 h-3 text-[#8F262B]" />
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        data-testid="demo-phone-input"
                      />
                    </div>

                    {/* Preferred Date */}
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        <Calendar className="w-3 h-3 text-emerald-400" />
                        Preferred Date *
                      </label>
                      <input
                        type="date"
                        name="preferred_date"
                        value={formData.preferred_date}
                        onChange={handleChange}
                        min={getMinDate()}
                        className="form-input"
                        required
                        data-testid="demo-date-input"
                      />
                    </div>

                    {/* Preferred Time */}
                    <div>
                      <label className="form-label flex items-center gap-1.5">
                        <Clock className="w-3 h-3 text-emerald-400" />
                        Preferred Time *
                      </label>
                      <select
                        name="preferred_time"
                        value={formData.preferred_time}
                        onChange={handleChange}
                        className="form-input"
                        required
                        data-testid="demo-time-select"
                      >
                        <option value="">Select time</option>
                        {timeSlots.map((time) => (
                          <option key={time} value={time}>{time}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Service Interest */}
                  <div>
                    <label className="form-label">Service of Interest *</label>
                    <select
                      name="service_interest"
                      value={formData.service_interest}
                      onChange={handleChange}
                      className="form-input"
                      required
                      data-testid="demo-service-select"
                    >
                      <option value="">Select a service</option>
                      {serviceOptions.map((service) => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="form-label flex items-center gap-1.5">
                      <MessageSquare className="w-3 h-3 text-slate-400" />
                      Additional Information
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={2}
                      className="form-input resize-none"
                      placeholder="Tell us about your project..."
                      data-testid="demo-message-input"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="btn-secondary flex-1 py-2.5 text-sm"
                      data-testid="demo-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary flex-1 py-2.5 text-sm disabled:opacity-50"
                      data-testid="demo-submit-btn"
                    >
                      {isSubmitting ? 'Submitting...' : 'Schedule Demo'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
